import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { ru } from 'date-fns/locale';

export function money(cents: number, currency = 'RUB', from = false) {
  const v = new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(
    cents / 100,
  );
  return from ? `от ${v}` : v;
}

export function duration(minutes: number) {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const d = minutes / 1440;
    return `${d} ${plural(d, 'день', 'дня', 'дней')}`;
  }
  if (minutes >= 1440) {
    const d = Math.floor(minutes / 1440);
    const h = Math.round((minutes % 1440) / 60);
    return `${d} ${plural(d, 'день', 'дня', 'дней')}${h ? ` ${h} ч` : ''}`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} мин`;
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

export function plural(n: number, one: string, few: string, many: string) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

/** Wall-clock formatting in the studio's timezone (never the device's). */
export const fmt = (iso: string | Date, tz: string, pattern: string) => formatInTimeZone(iso, tz, pattern, { locale: ru });

export const dayLabel = (isoDate: string, tz: string) => fmt(fromZonedTime(`${isoDate}T12:00:00`, tz), tz, 'EEEEEE, d MMM');
export const dateTimeLabel = (iso: string, tz: string) => fmt(iso, tz, 'd MMMM, EEEE, HH:mm');
export const timeLabel = (iso: string, tz: string) => fmt(iso, tz, 'HH:mm');

/** Today's date (YYYY-MM-DD) in the studio's timezone. */
export const todayIn = (tz: string, offsetDays = 0) =>
  formatInTimeZone(new Date(Date.now() + offsetDays * 86400e3), tz, 'yyyy-MM-dd');

/** studio-local date + HH:mm → absolute ISO instant */
export const zonedIso = (date: string, time: string, tz: string) => fromZonedTime(`${date}T${time}:00`, tz).toISOString();

export function addDays(isoDate: string, n: number) {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Monday of the ISO week containing isoDate. */
export function weekStart(isoDate: string) {
  const d = new Date(`${isoDate}T12:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  return addDays(isoDate, -dow);
}

export const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/** "+7 916 123-45-67" from "+79161234567" */
export function prettyPhone(e164: string) {
  const m = /^\+7(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(e164);
  return m ? `+7 ${m[1]} ${m[2]}-${m[3]}-${m[4]}` : e164;
}

export const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

export const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Подтверждена',
  arrived: 'Принята',
  completed: 'Готова',
  cancelled: 'Отменена',
  no_show: 'Не приехал',
};
